import React, { useState, useCallback } from 'react';
import './SmartPromptBuilder.css';

// ── Dropdown options ─────────────────────────────────────────────────────────
const SUBJECTS = [
  { value: '', label: 'Subject…' },
  { value: 'fierce wolf', label: '🐺 Wolf' },
  { value: 'cosmic dragon', label: '🐉 Dragon' },
  { value: 'mystic cat', label: '🐱 Cat' },
  { value: 'cunning fox', label: '🦊 Fox' },
  { value: 'rising phoenix', label: '🔥 Phoenix' },
  { value: 'lone astronaut', label: '🚀 Astronaut' },
  { value: 'ancient samurai', label: '⚔️ Samurai' },
  { value: 'enchanted mermaid', label: '🧜 Mermaid' },
  { value: 'sleek robot', label: '🤖 Robot' },
  { value: 'roaring tiger', label: '🐯 Tiger' },
  { value: 'wise owl', label: '🦉 Owl' },
  { value: 'ornate skull', label: '💀 Skull' },
  { value: 'majestic eagle', label: '🦅 Eagle' },
  { value: 'mythic unicorn', label: '🦄 Unicorn' },
  { value: 'deep-sea octopus', label: '🐙 Octopus' },
];

const STYLES = [
  { value: '', label: 'Style…' },
  { value: 'cyberpunk neon style', label: '⚡ Cyberpunk' },
  { value: 'loose watercolor paint style', label: '🎨 Watercolor' },
  { value: 'vintage retro 70s poster style', label: '📼 Vintage / Retro' },
  { value: 'clean minimalist line-art style', label: '◻️ Minimalist' },
  { value: 'dark gothic style', label: '🖤 Gothic' },
  { value: 'sharp geometric vector style', label: '🔷 Geometric' },
  { value: 'synthwave vaporwave style', label: '🌆 Synthwave' },
  { value: 'steampunk brass-and-copper style', label: '⚙️ Steampunk' },
  { value: 'bold comic-book pop-art style', label: '💥 Comic / Pop Art' },
  { value: 'flat anime style', label: '🌸 Anime' },
  { value: 'psychedelic surrealist style', label: '🌀 Psychedelic' },
  { value: 'detailed oil-painting style', label: '🖼️ Oil Painting' },
];

const MOODS = [
  { value: '', label: 'Mood…' },
  { value: 'dark and mysterious atmosphere', label: '🌑 Dark & Mysterious' },
  { value: 'bright and energetic atmosphere', label: '☀️ Bright & Energetic' },
  { value: 'peaceful and serene atmosphere', label: '🌿 Peaceful & Serene' },
  { value: 'epic and powerful atmosphere', label: '💪 Epic & Powerful' },
  { value: 'playful and fun atmosphere', label: '🎉 Playful & Fun' },
  { value: 'romantic and dreamy atmosphere', label: '💫 Romantic & Dreamy' },
  { value: 'eerie and haunting atmosphere', label: '👻 Eerie & Haunting' },
  { value: 'futuristic and cold atmosphere', label: '🤖 Futuristic & Cold' },
  { value: 'warm and nostalgic atmosphere', label: '🍂 Warm & Nostalgic' },
  { value: 'majestic and awe-inspiring atmosphere', label: '✨ Majestic & Awe-Inspiring' },
];

const COLOR_PALETTES = [
  { value: '', label: 'Color Palette…' },
  { value: 'neon color palette', label: '🌈 Neon' },
  { value: 'soft pastel color palette', label: '🌸 Pastel' },
  { value: 'earth tones palette', label: '🌱 Earth Tones' },
  { value: 'monochrome black-and-white palette', label: '⬛ Monochrome' },
  { value: 'fiery gold and orange palette', label: '🔥 Fire & Gold' },
  { value: 'deep ocean blue palette', label: '🌊 Ocean Blue' },
  { value: 'sunset pink and orange palette', label: '🌅 Sunset' },
  { value: 'galaxy violet and indigo palette', label: '🔮 Galaxy Purple' },
  { value: 'forest green palette', label: '🌲 Forest Green' },
  { value: 'rose gold and blush palette', label: '🌹 Rose Gold' },
  { value: 'ice blue and silver palette', label: '❄️ Ice & Silver' },
];

// Build a prompt string from the current selections
function buildPrompt(subject, style, mood, color) {
  const parts = [subject, style, mood, color].filter(Boolean);
  return parts.join(', ');
}

/**
 * SmartPromptBuilder
 * Props:
 *   onPromptChange(text: string) — called whenever the built prompt changes
 *   disabled: bool
 */
export default function SmartPromptBuilder({ onPromptChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [style, setStyle] = useState('');
  const [mood, setMood] = useState('');
  const [color, setColor] = useState('');

  const handleChange = useCallback(
    (field, value, setter) => {
      setter(value);
      // Build with the new value inline (state update is async)
      const next = {
        subject: field === 'subject' ? value : subject,
        style: field === 'style' ? value : style,
        mood: field === 'mood' ? value : mood,
        color: field === 'color' ? value : color,
      };
      const built = buildPrompt(next.subject, next.style, next.mood, next.color);
      onPromptChange(built);
    },
    [subject, style, mood, color, onPromptChange]
  );

  const handleReset = () => {
    setSubject('');
    setStyle('');
    setMood('');
    setColor('');
    onPromptChange('');
  };

  const hasSelections = subject || style || mood || color;

  return (
    <div className={`spb-wrap${open ? ' spb-wrap--open' : ''}`}>
      {/* ── Toggle header ─────────────────────────────────────── */}
      <button
        type="button"
        className="spb-toggle"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-controls="spb-panel"
      >
        <span className="spb-toggle-icon">🔨</span>
        <span className="spb-toggle-label">Build prompt</span>
        {hasSelections && !open && (
          <span className="spb-badge">{[subject, style, mood, color].filter(Boolean).length}</span>
        )}
        <span className={`spb-chevron${open ? ' spb-chevron--up' : ''}`}>▾</span>
      </button>

      {/* ── Collapsible panel ─────────────────────────────────── */}
      <div
        id="spb-panel"
        className={`spb-panel${open ? ' spb-panel--open' : ''}`}
        aria-hidden={!open}
      >
        <div className="spb-grid">
          {/* Subject */}
          <div className="spb-field">
            <label className="spb-label">Subject</label>
            <select
              className="spb-select"
              value={subject}
              onChange={e => handleChange('subject', e.target.value, setSubject)}
              disabled={disabled || !open}
            >
              {SUBJECTS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Style */}
          <div className="spb-field">
            <label className="spb-label">Style</label>
            <select
              className="spb-select"
              value={style}
              onChange={e => handleChange('style', e.target.value, setStyle)}
              disabled={disabled || !open}
            >
              {STYLES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Mood */}
          <div className="spb-field">
            <label className="spb-label">Mood</label>
            <select
              className="spb-select"
              value={mood}
              onChange={e => handleChange('mood', e.target.value, setMood)}
              disabled={disabled || !open}
            >
              {MOODS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Color Palette */}
          <div className="spb-field">
            <label className="spb-label">Color Palette</label>
            <select
              className="spb-select"
              value={color}
              onChange={e => handleChange('color', e.target.value, setColor)}
              disabled={disabled || !open}
            >
              {COLOR_PALETTES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Preview + Reset */}
        {hasSelections && (
          <div className="spb-preview-row">
            <span className="spb-preview-text">
              "{buildPrompt(subject, style, mood, color)}"
            </span>
            <button
              type="button"
              className="spb-reset"
              onClick={handleReset}
              disabled={disabled}
              title="Clear all selections"
            >
              ✕ Clear
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
